import { useState, useEffect, useCallback } from 'react';
import { fetchAgents, createAgent as apiCreateAgent } from '../api/agents';
import { fetchProviders, fetchProviderModels } from '../api/providers';
import { buildDefaultSkillPayload } from '../constants/default-agent-skills';
import type { Agent } from '../types/agent.types';
import type { Provider, ProviderModel } from '../types/provider.types';
import { getHostName } from '../tools/office-tools-registry';

const DEFAULT_AGENT_NAME = 'Default';
const DEFAULT_SYSTEM_PROMPT = 'You are a helpful AI assistant.';

const PLATFORM_SYSTEM_PROMPTS: Record<string, string> = {
  powerpoint: `You are a strict, rule-bound AI assistant for PowerPoint presentations.

You MUST ALWAYS use the "presidio-template" skill when creating slides.
You are NOT allowed to create plain or generic slides under any circumstances UNLESS the user explicitly requests a different style or plain slides.

Before generating any slide:
- You MUST call \`read_skill_content("presidio-template")\`
- You MUST fully understand the available layouts, assets, and constraints

After that:
- You MUST ONLY use \`generate_template_slide\` to create slides
- You MUST select the most appropriate layout from the template
- You MUST strictly adhere to all template rules, fields, and limitations

Strictly prohibited:
- Skipping \`read_skill_content\`
- Creating slides without using the template
- Inventing layouts, fields, or formats not defined in the template
- Outputting raw slide content instead of using the tool

These rules are MANDATORY and must be followed at all times.`,

  word: `You are a Word document assistant. Help users draft, edit, format, and analyze documents inside Microsoft Word. Use the available Word tools to read and modify the document directly. When making changes, explain what you're about to do before executing.`,

  excel: `You are an Excel spreadsheet assistant. Help users analyze data, create formulas, build charts, and organize worksheets inside Microsoft Excel. When working with data, read the relevant cells first to understand the structure before suggesting changes. Explain formulas in plain language. Use excel_batch_operations when performing multiple operations for better performance.`,
};

function getPlatformSystemPrompt(platformName?: string | null): string {
  if (!platformName) return DEFAULT_SYSTEM_PROMPT;
  return PLATFORM_SYSTEM_PROMPTS[platformName.toLowerCase()] ?? DEFAULT_SYSTEM_PROMPT;
}

function getPlatformAgentName(platformName?: string | null): string {
  if (!platformName) return DEFAULT_AGENT_NAME;
  const normalized = platformName.toLowerCase();
  const displayName = normalized === 'powerpoint'
    ? 'PPT'
    : normalized === 'word'
    ? 'Word'
    : normalized === 'excel'
    ? 'Excel'
    : platformName;
  return `${displayName} generator (default)`;
}

// Slug patterns for provider and model matching (case-insensitive)
const PATH_SLUGS = ['path', 'presidio-ai', 'presidio-ai-labs'];
const MINIMAX_PATTERN = /minimax/i;
const BEDROCK_SLUGS = ['bedrock', 'aws-bedrock', 'amazon-bedrock'];
const SONNET_PATTERN = /sonnet/i;
const CLAUDE_PATTERN = /claude/i;

// Preferred MiniMax model slugs (highest priority for default agents — budget-exempt)
const PREFERRED_MINIMAX_SLUGS = [
  'PATH/minimax-m2-5',
  'minimax-m2-5',
  'minimax-m2.5',
];

// Preferred Sonnet model slugs as fallback (highest version first, non-1M preferred)
const PREFERRED_MODEL_SLUGS = [
  'claude-4.6-sonnet-bedrock',
  'claude-4.6-sonnet-bedrock-1m',
  'claude-4.5-sonnet-bedrock',
  'claude-4.5-sonnet-bedrock-1m',
  'claude-4-sonnet-bedrock',
  'claude-4-sonnet-bedrock-1m',
  'claude-3.7-sonnet-bedrock',
  'claude-4-sonnet',
  'claude-3.7-sonnet',
];

/**
 * Search across all providers for the best provider + model combination.
 * Priority:
 *   1. PATH provider with MiniMax 2.5 (budget-exempt)
 *   2. Bedrock provider with a Sonnet model
 *   3. Any provider with a Sonnet model
 *   4. Platform-managed provider with its default model
 *   5. Any provider with any active model
 */
async function findBestProviderAndModel(
  wid: string,
  providers: Provider[],
): Promise<{ provider: Provider; model: ProviderModel }> {

  // Collect models per provider (use embedded if available, otherwise fetch)
  const providerModels = new Map<number, ProviderModel[]>();
  for (const p of providers) {
    let models = p.providerModels?.filter((m) => m.isActive) ?? [];
    if (models.length === 0) {
      try {
        models = (await fetchProviderModels(wid, p.id)).filter((m) => m.isActive);
      } catch {
        // Skip providers whose models can't be fetched
      }
    }
    if (models.length > 0) {
      providerModels.set(p.id, models);
    }
  }

  const isPath = (p: Provider) => {
    const slug = (p.providerTemplate?.slug ?? p.displayName ?? '').toLowerCase();
    return PATH_SLUGS.some((s) => slug.includes(s));
  };

  const isBedrock = (p: Provider) => {
    const slug = (p.providerTemplate?.slug ?? p.displayName ?? '').toLowerCase();
    return BEDROCK_SLUGS.some((b) => slug.includes(b));
  };

  // Pick the best MiniMax model by checking preferred slugs, then any minimax match
  const findMinimax = (models: ProviderModel[]): ProviderModel | undefined => {
    for (const slug of PREFERRED_MINIMAX_SLUGS) {
      const match = models.find((m) => m.slug === slug);
      if (match) return match;
    }
    return models.find((m) => MINIMAX_PATTERN.test(m.slug) || MINIMAX_PATTERN.test(m.name));
  };

  // Pick the best Sonnet model by checking preferred slugs in order (4.6 → 4.5 → 4 → 3.7),
  // then falling back to any model whose slug/name contains "sonnet"
  const findSonnet = (models: ProviderModel[]): ProviderModel | undefined => {
    for (const slug of PREFERRED_MODEL_SLUGS) {
      const match = models.find((m) => m.slug === slug);
      if (match) return match;
    }
    return models.find((m) => SONNET_PATTERN.test(m.slug) || SONNET_PATTERN.test(m.name));
  };

  const findClaude = (models: ProviderModel[]) =>
    models.find((m) => CLAUDE_PATTERN.test(m.slug) || CLAUDE_PATTERN.test(m.name));

  // 1. MiniMax 2.5 on any provider (budget-exempt, preferred for Office Add-in)
  // Check PATH providers first, then fall back to any provider with a MiniMax model
  const pathFirst = [...providers].sort((a, b) => {
    const aPath = isPath(a) ? 0 : 1;
    const bPath = isPath(b) ? 0 : 1;
    return aPath - bPath;
  });
  for (const p of pathFirst) {
    const models = providerModels.get(p.id) ?? [];
    const minimax = findMinimax(models);
    if (minimax) {
      return { provider: p, model: minimax };
    }
  }

  // 2. Bedrock provider + Sonnet model
  for (const p of providers) {
    if (!isBedrock(p)) continue;
    const models = providerModels.get(p.id) ?? [];
    const sonnet = findSonnet(models);
    if (sonnet) {
      return { provider: p, model: sonnet };
    }
    // Bedrock but no sonnet — try any Claude model on Bedrock
    const claude = findClaude(models);
    if (claude) {
      return { provider: p, model: claude };
    }
  }

  // 3. Any provider with a Sonnet model
  for (const p of providers) {
    const models = providerModels.get(p.id) ?? [];
    const sonnet = findSonnet(models);
    if (sonnet) {
      return { provider: p, model: sonnet };
    }
  }

  // 4. Platform-managed provider with default or first model
  const platform = providers.find((p) => p.isPlatformManaged);
  if (platform) {
    const models = providerModels.get(platform.id) ?? [];
    const model =
      models.find((m) => m.isDefault) ??
      findClaude(models) ??
      models[0];
    if (model) {
      return { provider: platform, model };
    }
  }

  // 5. Any provider with any model
  for (const p of providers) {
    const models = providerModels.get(p.id) ?? [];
    if (models.length > 0) {
      const model = models.find((m) => m.isDefault) ?? models[0];
      return { provider: p, model };
    }
  }

  throw new Error('No providers with active models available. Please configure a provider in Settings.');
}

/**
 * Ensures a "Default" agent exists for the workspace.
 * If no agents exist, auto-creates one targeting Bedrock Sonnet.
 */
export function useDefaultAgent(workspaceId: string | null, platformName?: string | null) {
  const [defaultAgent, setDefaultAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async (wid: string) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch existing agents
      const { agents } = await fetchAgents(wid);

      const resolvedPlatformName = platformName ?? getHostName();
      const platformAgentName = getPlatformAgentName(resolvedPlatformName);

      // 2. Look for platform default name, then legacy "Default", then isDefault flag, then first non-archived
      const existing =
        agents.find((a) => a.name === platformAgentName && !a.archivedAt) ??
        agents.find((a) => a.name === DEFAULT_AGENT_NAME && !a.archivedAt) ??
        agents.find((a) => a.isDefault && !a.archivedAt) ??
        agents.find((a) => !a.archivedAt);

      if (existing) {
        setDefaultAgent(existing);
        return;
      }

      // 3. No usable agents — create "Default" with best provider/model
      const providers = await fetchProviders(wid);
      if (providers.length === 0) {
        throw new Error('No providers available. Please configure a provider in Settings.');
      }

      const { provider, model } = await findBestProviderAndModel(wid, providers);

      const skillsPayload = await buildDefaultSkillPayload(wid, resolvedPlatformName);

      const agent = await apiCreateAgent(wid, {
        name: platformAgentName,
        description: 'Default AI assistant',
        config: {
          systemPrompt: getPlatformSystemPrompt(resolvedPlatformName),
          maxTokens: 8192,
          temperature: 0.7,
        },
        providerId: provider.id,
        providerModelId: model.id,
        ...(skillsPayload ? { skills: skillsPayload } : {}),
      });

      setDefaultAgent(agent);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set up default agent';
      setError(message);
      console.error('[useDefaultAgent]', message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (workspaceId) {
      resolve(workspaceId);
    }
  }, [workspaceId, resolve]);

  const retry = useCallback(() => {
    if (workspaceId) {
      resolve(workspaceId);
    }
  }, [workspaceId, resolve]);

  return { defaultAgent, loading, error, retry };
}
